/**
 * Activation failure → an operator-facing reason AND the way out.
 *
 * `JsonClient` throws `HttpError`, whose `message` is the request line
 * ("POST /api/activation/activate -> 403") — the transport, not the cause. Login rendered
 * that verbatim, so the single most common activation failure, the DEVICE CAP, reached the
 * operator as an HTTP status: no statement of what was refused, and no hint that the fix
 * (deactivate an old device) exists at all.
 *
 * The cloud's codes live in `cloud/app/activation/service.py` (`ActivationError.code`) and
 * are mapped to HTTP by `cloud/app/activation/router.py`:
 *   - `device_cap_exceeded`  → 403 `{"detail": "Device cap exceeded for tier"}`
 *   - `seat_limit_reached`   → 403 `{"detail": "seat_limit_reached"}`
 *   - `invalid_license` / `license_expired` / `no_license`
 *                            → 404 `{"detail": "License not found or expired"}`
 * The `detail` string is the only machine-readable signal on the wire today (the router
 * does not emit a `code` field), so it is what we match on — see the hand-off note in the
 * task report about giving the cloud a structured code.
 */

/** Where a device is released. Admin-only surface, so non-admins get the "ask" variant. */
const DEACTIVATE_WHERE = 'Admin → "Activation & licensing" → Deactivate';

/** The `detail` payload FastAPI puts on an HTTPException. */
function detailOf(error: unknown): string {
  const body = (error as { body?: unknown } | null)?.body;
  const detail = (body as { detail?: unknown } | null)?.detail;
  return typeof detail === "string" ? detail : "";
}

function statusOf(error: unknown): number | undefined {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === "number" ? status : undefined;
}

function rawMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * A human sentence for an activation failure: what the cloud refused, and what to do next.
 * Unrecognised failures fall through to the raw message (never swallowed).
 */
export function activationErrorMessage(error: unknown): string {
  const status = statusOf(error);
  const detail = detailOf(error);

  if (status === 403 && /device[ _]cap/i.test(detail)) {
    return (
      "This workspace has reached its device limit, so this computer could not be activated. " +
      `Free a slot by deactivating a device you no longer use (${DEACTIVATE_WHERE}), then sign in again — ` +
      "if you are not a workspace admin, ask one to do it or to raise the device cap."
    );
  }
  if (status === 403 && /seat[ _]limit/i.test(detail)) {
    return (
      "This workspace has no licence seats left, so this account could not be activated. " +
      "Ask your workspace admin to free a seat or add one, then sign in again."
    );
  }
  if (status === 404 && /licen[cs]e/i.test(detail)) {
    return (
      "No valid licence was found for this workspace (it may have expired). " +
      "Ask your workspace admin to check the licence, then sign in again."
    );
  }
  // Unknown failure: the raw message beats an invented explanation, but say what it was for.
  return `Device activation was refused: ${detail || rawMessage(error)}`;
}
